import api from '../redux-api';
import { Action, ApiAction, AsyncResult } from '../index';

// Types
import PortfolioHolding from '@/types/portfolio-holding';
import { AIStatementReaderState } from './types/ai-statement-reader';
import { batchAddHoldingsToPortfolio } from './edit-portfolio';

// Utils
import { logCancelAIStatementReaderProcess, logDocumentsProcessedByAIStatementReader, logErrorInAIStatementReader } from '@/services/usage-log';
import { startLoaderAction, stopLoaderAction } from './ui/loaders';

const initialState: AIStatementReaderState = {
  current: {
    processedFiles: [],
    processingCancelled: false,
    processingError: false,
  },
};

export const PROCESS_PORTFOLIO: ApiAction = {
  type: 'connect/ai-statements-reader/process-portfolio',
  endpoint: 'ai-statement-reader/portfolio/',
};

export const RESET_PROCESSING: Action = {
  type: 'connect/ai-statements-reader/reset-processing',
};

export const CANCEL_PROCESSING: Action = {
  type: 'connect/ai-statements-reader/cancel-processing',
};

export const RESET_READ_END_TIME: Action = {
  type: 'connect/ai-statements-reader/reset-read-end-time',
};

export const SET_READ_END_TIME: Action = {
  type: 'connect/ai-statements-reader/set-read-end-time',
};

export const RESET_READ_START_TIME: Action = {
  type: 'connect/ai-statements-reader/reset-read-start-time',
};

export const SET_READ_START_TIME: Action = {
  type: 'connect/ai-statements-reader/set-read-start-time',
};

export default function reducer(state = initialState, action: Action) {
  switch (action.type) {
    case RESET_PROCESSING.type: {
      return {
        ...state,
        current: {
          ...state.current,
          processedFiles: [],
          processingCancelled: false,
          processingError: false,
        },
      };
    }
    case CANCEL_PROCESSING.type: {
      return {
        ...state,
        current: {
          ...state.current,
          processingCancelled: true,
        },
      };
    }
    case `${PROCESS_PORTFOLIO.type}_SUCCESS`: {
      return {
        ...state,
        current: {
          ...state.current,
          processedFiles: state.current.processingCancelled ? [] : action.data.data,
        },
      };
    }
    case `${PROCESS_PORTFOLIO.type}_ERROR`: {
      return {
        ...state,
        current: {
          ...state.current,
          processedFiles: [],
          processingError: !state.current.processingCancelled,
        },
      };
    }
    case RESET_READ_END_TIME.type: {
      return {
        ...state,
        current: {
          ...state.current,
          activityLogs: {
            ...state.current.activityLogs,
            aiStatementReadEndTime: undefined,
          },
        },
      };
    }
    case SET_READ_END_TIME.type: {
      return {
        ...state,
        current: {
          ...state.current,
          activityLogs: {
            ...state.current.activityLogs,
            aiStatementReadEndTime: action.time,
          },
        },
      };
    }
    case RESET_READ_START_TIME.type: {
      return {
        ...state,
        current: {
          ...state.current,
          activityLogs: {
            ...state.current.activityLogs,
            aiStatementReadStartTime: undefined,
          },
        },
      };
    }
    case SET_READ_START_TIME.type: {
      return {
        ...state,
        current: {
          ...state.current,
          activityLogs: {
            ...state.current.activityLogs,
            aiStatementReadStartTime: action.time,
          },
        },
      };
    }
    default:
      return state;
  }
}

export const processPortfolioFiles =
  (files: File[]): AsyncResult<void> =>
  async (dispatch, getState) => {
    // Reset processing state to allow cancellation
    dispatch(resetProcessing());

    const formData = new FormData();
    const startTime = Date.now();
    // Only sending one file for now
    formData.append('file', files[0]);

    // TODO: make sure to check with the API data once it's ready.
    api
      .post(PROCESS_PORTFOLIO, null, formData)
      .then(({ data }) => {
        const { activityLogs, processingCancelled } = getState().aiStatementReader.current;
        const { aiStatementReadEndTime, aiStatementReadStartTime } = activityLogs || {};

        // Log documents processed by AI Statement Reader only when the process is not cancelled
        if (!processingCancelled) {
          logDocumentsProcessedByAIStatementReader(formData, data, aiStatementReadEndTime, aiStatementReadStartTime);
        }
      })
      .catch(error => {
        logErrorInAIStatementReader(error, startTime);
        dispatch(resetReadStartTime());
      })
      .finally(() => dispatch(stopLoaderAction(PROCESS_PORTFOLIO.type)));
  };

export const convertProcessedStatementToPortfolio =
  (instruments: Array<PortfolioHolding>): AsyncResult<void> =>
  async dispatch => {
    await dispatch(batchAddHoldingsToPortfolio(instruments));
  };

export const cancelAIStatementReaderProcessing =
  (files: File[], isProcessing?: boolean, isProcessed?: boolean): AsyncResult<void> =>
  async (dispatch, getState) => {
    const { activityLogs } = getState().aiStatementReader.current;
    const aiStatementReadStartTime = activityLogs?.aiStatementReadStartTime;

    // Reset dates if the files are not processed
    if (!isProcessed) {
      dispatch(resetReadStartTime());

      // Log cancellation only when is processing the files
      if (isProcessing) {
        logCancelAIStatementReaderProcess(files, aiStatementReadStartTime);
      }
    }

    // Clear current state
    dispatch(resetProcessing());

    // Stop background processing from updating state
    dispatch({ type: CANCEL_PROCESSING.type });
  };

export const resetProcessing = (): AsyncResult<void> => async dispatch => {
  dispatch({ type: RESET_PROCESSING.type });
};

export const resetReadEndTime = (): AsyncResult<void> => async dispatch => {
  dispatch({ type: RESET_READ_END_TIME.type });
};

export const resetReadStartTime = (): AsyncResult<void> => async dispatch => {
  dispatch(stopLoaderAction(PROCESS_PORTFOLIO.type));
  dispatch(resetReadEndTime());
  dispatch({ type: RESET_READ_START_TIME.type });
};

export const setReadEndTime = (): AsyncResult<void> => async dispatch => {
  dispatch({ type: SET_READ_END_TIME.type, time: Date.now() });
};

export const setReadStartTime = (): AsyncResult<void> => async dispatch => {
  dispatch(startLoaderAction(PROCESS_PORTFOLIO.type));
  dispatch(resetReadEndTime());
  dispatch({ type: SET_READ_START_TIME.type, time: Date.now() });
};
